/**
 * PanelManager - Blender-style resizable, draggable panel system
 * Manages floating workspace panels with drag, resize, and toggle capabilities
 */

export class PanelManager {
  private panels: Map<string, HTMLElement> = new Map();
  private dragState: {
    panel: HTMLElement | null;
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
  } = { panel: null, startX: 0, startY: 0, startLeft: 0, startTop: 0 };

  private resizeState: {
    panel: HTMLElement | null;
    handle: string | null;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } = { panel: null, handle: null, startX: 0, startY: 0, startWidth: 0, startHeight: 0 };

  constructor() {
    this.initializePanels();
    this.setupEventListeners();
  }

  private initializePanels() {
    // Register floating panels only (preview is now docked, not floating)
    const panelIds = ['sequenceSettingsPanel', 'performancePanel'];

    panelIds.forEach(id => {
      const panel = document.getElementById(id);
      if (panel) {
        this.panels.set(id, panel);
        this.makeResizable(panel);
        this.makeDraggable(panel);
      } else {
        console.warn(`[PanelManager] Panel not found: ${id}`);
      }
    });
  }

  private setupEventListeners() {
    // Global mouse handlers for drag and resize
    document.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    document.addEventListener('mouseup', () => this.handleMouseUp());
  }

  private makeDraggable(panel: HTMLElement) {
    const titlebar = panel.querySelector('.panel-titlebar') as HTMLElement;
    if (!titlebar) return;

    titlebar.addEventListener('mousedown', (e) => {
      // Ignore if clicking on control buttons
      if ((e.target as HTMLElement).closest('.panel-control-btn')) return;

      e.preventDefault();
      this.startDrag(panel, e);
    });
  }

  private makeResizable(panel: HTMLElement) {
    const handles = panel.querySelectorAll('.resize-handle');

    handles.forEach(handle => {
      handle.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation(); // Don't trigger drag

        const handleType = handle.classList.contains('right') ? 'right' :
                          handle.classList.contains('bottom') ? 'bottom' : 'corner';

        this.startResize(panel, handleType, e as MouseEvent);
      });
    });
  }

  private startDrag(panel: HTMLElement, e: MouseEvent) {
    panel.classList.add('dragging');

    const rect = panel.getBoundingClientRect();

    this.dragState = {
      panel,
      startX: e.clientX,
      startY: e.clientY,
      startLeft: rect.left,
      startTop: rect.top
    };

    // Bring to front
    this.bringToFront(panel);
  }

  private startResize(panel: HTMLElement, handle: string, e: MouseEvent) {
    const rect = panel.getBoundingClientRect();

    this.resizeState = {
      panel,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: rect.width,
      startHeight: rect.height
    };

    // Add active class to handle
    panel.querySelectorAll('.resize-handle').forEach(h => {
      if (h.classList.contains(handle === 'corner' ? 'corner' : handle)) {
        h.classList.add('active');
      }
    });

    // Bring to front
    this.bringToFront(panel);
  }

  private handleMouseMove(e: MouseEvent) {
    // Handle dragging
    if (this.dragState.panel) {
      const deltaX = e.clientX - this.dragState.startX;
      const deltaY = e.clientY - this.dragState.startY;

      let newLeft = this.dragState.startLeft + deltaX;
      let newTop = this.dragState.startTop + deltaY;

      // Keep within viewport
      const maxLeft = window.innerWidth - this.dragState.panel.offsetWidth;
      const maxTop = window.innerHeight - this.dragState.panel.offsetHeight;

      newLeft = Math.max(0, Math.min(newLeft, maxLeft));
      newTop = Math.max(0, Math.min(newTop, maxTop));

      this.dragState.panel.style.left = newLeft + 'px';
      this.dragState.panel.style.top = newTop + 'px';
      this.dragState.panel.style.right = 'auto';
      this.dragState.panel.style.bottom = 'auto';
    }

    // Handle resizing
    if (this.resizeState.panel && this.resizeState.handle) {
      const deltaX = e.clientX - this.resizeState.startX;
      const deltaY = e.clientY - this.resizeState.startY;

      const minWidth = 250;
      const minHeight = 200;

      if (this.resizeState.handle === 'right' || this.resizeState.handle === 'corner') {
        const newWidth = Math.max(minWidth, this.resizeState.startWidth + deltaX);
        this.resizeState.panel.style.width = newWidth + 'px';
      }

      if (this.resizeState.handle === 'bottom' || this.resizeState.handle === 'corner') {
        const newHeight = Math.max(minHeight, this.resizeState.startHeight + deltaY);
        this.resizeState.panel.style.height = newHeight + 'px';
      }
    }
  }

  private handleMouseUp() {
    // End drag
    if (this.dragState.panel) {
      this.dragState.panel.classList.remove('dragging');
      this.dragState.panel = null;
    }

    // End resize
    if (this.resizeState.panel) {
      this.resizeState.panel.querySelectorAll('.resize-handle').forEach(h => {
        h.classList.remove('active');
      });
      this.resizeState.panel = null;
      this.resizeState.handle = null;
    }
  }

  private bringToFront(panel: HTMLElement) {
    // Reset all panels to base z-index
    this.panels.forEach(p => {
      p.style.zIndex = '100';
    });

    // Bring clicked panel to front
    panel.style.zIndex = '200';
  }

  /**
   * Toggle panel visibility
   */
  togglePanel(panelId: string): boolean {
    const panel = this.panels.get(panelId);
    if (!panel) {
      console.error(`[PanelManager] Panel not found for toggle: ${panelId}`);
      return false;
    }

    const isHidden = panel.classList.contains('hidden');

    if (isHidden) {
      panel.classList.remove('hidden');
      this.bringToFront(panel);
      return true; // Now visible
    } else {
      panel.classList.add('hidden');
      return false; // Now hidden
    }
  }

  /**
   * Show panel
   */
  showPanel(panelId: string) {
    const panel = this.panels.get(panelId);
    if (panel) {
      panel.classList.remove('hidden');
      this.bringToFront(panel);

      // Dispatch panel-shown event after DOM has updated for resize listeners
      requestAnimationFrame(() => {
        panel.dispatchEvent(new CustomEvent('panel-shown', { bubbles: true }));
      });
    }
  }

  /**
   * Hide panel
   */
  hidePanel(panelId: string) {
    const panel = this.panels.get(panelId);
    if (panel) {
      panel.classList.add('hidden');
    }
  }

  /**
   * Check if panel is visible
   */
  isPanelVisible(panelId: string): boolean {
    const panel = this.panels.get(panelId);
    return panel ? !panel.classList.contains('hidden') : false;
  }

  /**
   * Reset panel to default position and size
   */
  resetPanel(panelId: string) {
    const panel = this.panels.get(panelId);
    if (!panel) return;

    // Remove inline styles to use CSS defaults
    panel.style.left = '';
    panel.style.top = '';
    panel.style.right = '';
    panel.style.bottom = '';
    panel.style.width = '';
    panel.style.height = '';
  }
}
